import commonUtilities from "utilities/common";
import { CONFIGURATION } from "utilities/configuration";

/**
 * Utility: derive lead number from current URL.
 */
function getLeadNumber() {
    const segments = window.location.pathname.split("/");
    const idx = segments.findIndex(s => s.toLowerCase() === "lead");
    return idx > -1 && segments.length > idx + 1 ? segments[idx + 1] : null;
}

/**
 * Create an Abortable Fetch Promise with timeout support.
 */
function fetchWithAbort(endpoint, { timeout = 10000, ...options } = {}) {
    const controller = new AbortController();
    const signal = controller.signal;

    let timeoutId;
    const timer = new Promise((_, reject) => {
        timeoutId = setTimeout(() => {
            controller.abort();
            reject(new Error("Request timed out"));
        }, timeout);
    });

    const fetchPromise = (async () => {
        try {
            const response = await fetch(endpoint, { ...options, signal });
            const contentType = response.headers.get("content-type") || "";

            // parse JSON if possible
            let body;
            if (contentType.includes("application/json")) {
                try {
                    body = await response.json();
                } catch {
                    body = null; // fallback if JSON parsing fails
                }
            } else if (contentType.includes("application/pdf")) {
                const reader = response.body.getReader();
                const chunks = [];
                while (true) {
                    const { done, value } = await reader.read();
                    if (done) break;
                    chunks.push(value);
                }
                body = { pdfContent: chunks };
            } else {
                const text = await response.text();;
                body = { text: text };
            }

            // include statusCode even on errors
            if (!response.ok) {
                const error = new Error(`HTTP ${response.status}`);
                error.statusCode = response.status;
                error.body = { ...body }; // attach JSON or text body
                throw error;
            }

            const statusCode = body?.statusCode || response.status;

            return { ...body, statusCode: statusCode };
        } 
        catch (error) {
            
            let err = new Error(error.message);
            err.name = error.name || error.message || "FetchError";
            err.body = error.body || { message: error.message, name: error.name };

            throw err;
        }
        finally {
            clearTimeout(timeoutId);
        }
    })();

    // Race fetch vs timeout.
    const promise = Promise.race([fetchPromise, timer]);
    promise.abort = () => controller.abort();
    promise.isAborted = false;

    controller.signal.addEventListener("abort", () => {
        promise.isAborted = true;
    });

    return promise;
}

/**
 * Core HTTP client with Okta token + lead header support.
 */
async function client(endpoint, { body, method = "GET", timeout = CONFIGURATION.DEFAULT_TIMEOUT, ...customConfig } = {}) {
    const tokenResponse = await commonUtilities.getOktaToken();
    const accessToken = tokenResponse?.accessToken;

    const headers = {
        Authorization: accessToken ? `Bearer ${accessToken}` : "",
        "Content-Type": "application/json",
        "Lead-Number": getLeadNumber(),
        ...customConfig.headers
    };

    const config = {
        method,
        headers,
        ...customConfig
    };

    if (body) config.body = JSON.stringify(body);

    if (process.env.NODE_ENV === "development") {
        console.debug(`[client] ${method} ${endpoint} (timeout: ${timeout || "default"}ms)`);
    }

    return fetchWithAbort(endpoint, { ...config, timeout });
}

// Shorthand methods
client.get = (endpoint, config) => client(endpoint, { ...config, method: "GET" });
client.post = (endpoint, body, config) => client(endpoint, { ...config, method: "POST", body });
client.put = (endpoint, body, config) => client(endpoint, { ...config, method: "PUT", body });
client.delete = (endpoint, config) => client(endpoint, { ...config, method: "DELETE" });

export function createClient(defaultTimeout = CONFIGURATION.DEFAULT_TIMEOUT) {
    return (endpoint, config) => client(endpoint, { timeout: defaultTimeout, ...config });
}

export default client;
